import { DEFAULT_PRO_FILTER_SET, Service, useDataService } from 'services/data.service';
import { ArticleVisibilityBreakdownTable } from 'pages/ArticleVisibility/components/ArticleVisibilityBreakDownTable/ArticleVisibilityBreakdownTable';
import { useDataRequest } from 'hooks/useDataRequest';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { useMemo } from 'react';
import { Table } from 'models/data';
import { MetricsSection as MetricsCardSection } from './MetricsCardSection/MetricsCardSection';
import { groupObjectByKey } from '../utils/sortArticleVisibilityCards';
import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';
import { buildMetricsSteps, ArticleVisibilityRow } from '../model';
import { transformArticleVisibilityTable } from '../utils/transformArticleVisibilityTable';
import { decorateSubTableDataRequest } from 'pages/ArticleVisibility/utils/fulfillmentRequestDecorators';

interface CombinedTableData {
  l1: Table;
  l2ByMarketplace: Record<string, Table>;
}

export function FilteredContent() {
  const { fetchTotals, fetchTable } = useDataService(Service.ArticleVisibility, {
    ...DEFAULT_PRO_FILTER_SET,
    creationTimeFilter: true,
    timeFilter: false,
    comparisonTimeFilter: false,
    groupByAttribute: false,
  });

  const requestData = useDataRequest();

  const totalsState = useDataServiceQueryResult(
    requestData,
    data => fetchTotals('main', data),
    data => data,
    ['article-visibility-totals-main']
  );

  const tableState = useDataServiceQueryResult<ArticleVisibilityRow[], CombinedTableData>(
    requestData,
    async dataRequest => {
      const l1Response = await fetchTable('main', dataRequest);

      if (l1Response.error || !l1Response.data) {
        return { data: null, error: l1Response.error };
      }

      const marketplaceNames = Object.values(l1Response.data.rows).map(row => row.name);

      const l2Results = await Promise.all(
        marketplaceNames.map(async marketplace => {
          const decoratedRequest = decorateSubTableDataRequest(dataRequest, marketplace);
          const response = await fetchTable('main/level/2', decoratedRequest);
          return { marketplace, response };
        })
      );

      const l2ByMarketplace: Record<string, Table> = {};
      for (const { marketplace, response } of l2Results) {
        if (!response.error && response.data) {
          l2ByMarketplace[marketplace] = response.data;
        }
      }

      return {
        data: { l1: l1Response.data, l2ByMarketplace },
        error: null,
      };
    },
    (responseData): ArticleVisibilityRow[] => {
      const marketplaceRows = transformArticleVisibilityTable(responseData.l1);

      return marketplaceRows.map(row => {
        const channelTable = responseData.l2ByMarketplace[row.name];
        const subRows = channelTable ? transformArticleVisibilityTable(channelTable) : [];
        return { ...row, subRows };
      });
    },
    ['article-visibility-table-combined']
  );

  const notOnlineChannels = useMemo(() => {
    if (!tableState.data) return [];
    return tableState.data
      .filter(row => row.expArtNumCD.value > 0 && row.asrOnlArtNumCD.value === 0 && row.asrNonArtNumCD.value === 0)
      .map(row => row.name);
  }, [tableState.data]);

  const hasOnlineMarketplace = useMemo(() => notOnlineChannels.length === 0, [notOnlineChannels]);
  const isOnlinePhaseLoading = tableState.isLoading && !tableState.data;

  const steps = useMemo(() => {
    if (!totalsState.data) return [];
    return buildMetricsSteps(totalsState.data);
  }, [totalsState.data]);

  const totalContent = useMemo(() => groupObjectByKey(steps, step => step.column), [steps]);

  return (
    <PageContainer>
      <PageSectionTitle
        titleId="articleVisibility.title"
        subtitleId="articleVisibility.subtitle"
        className="col-span-12"
      />
      <div className="col-span-12">
        {totalsState.isLoading && (
          <div className="flex w-full justify-center col-span-12">
            <LoadingSpinner />
          </div>
        )}

        {totalsState.isError && <Tile errorDetails={totalsState.error} />}

        {totalsState.data && !totalsState.isError && (
          <MetricsCardSection
            content={totalContent}
            hasOnlineMarketplace={hasOnlineMarketplace}
            marketplacesWithoutOnline={notOnlineChannels}
            isOnlinePhaseLoading={isOnlinePhaseLoading}
          />
        )}
      </div>

      <span />

      <div className="col-span-12">
        {tableState.isLoading && (
          <Tile>
            <div className="flex w-full justify-center min-h-[320px] items-center">
              <LoadingSpinner />
            </div>
          </Tile>
        )}

        {!tableState.isLoading && (
          <ArticleVisibilityBreakdownTable
            rows={tableState.data ?? []}
            isError={tableState.isError}
            errorMessage={tableState.error}
          />
        )}
      </div>
    </PageContainer>
  );
}
